import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/** Frontend audio cap (friendlier than the server's 12 MB base64 limit).
 *  ~9 MB raw = roughly 10 min at 128 kbps MP3 or 15 min at 64 kbps.        */
const MAX_AUDIO_BYTES = 9 * 1024 * 1024;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function fmtDuration(s) {
  if (!s || s <= 0) return "";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${r}`;
}

/** /voices/:id — single voice log: audio player + transcript + admin edit. */
export default function VoiceLogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [log, setLog] = useState(null);          // null=loading, false=404
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get(`/voice-logs/${id}`)
      .then((r) => setLog(r.data))
      .catch(() => setLog(false));
  }, [id]);

  const startEdit = () => { setDraft({ ...log }); setEditing(true); };
  const cancel = () => { setDraft(null); setEditing(false); };

  const save = async () => {
    setSaving(true);
    try {
      const { id: _ignore, updated_at: _u, ...patch } = draft;
      const r = await api.put(`/voice-logs/${log.id}`, patch);
      setLog(r.data);
      setEditing(false);
      setDraft(null);
      toast.success("Voice log sealed.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const onAudioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_AUDIO_BYTES) {
      toast.error(`Audio too large (max ${Math.round(MAX_AUDIO_BYTES / 1024 / 1024)} MB).`);
      e.target.value = "";
      return;
    }
    try {
      const b64 = await fileToBase64(file);
      // Probe playback duration before committing — best-effort, browsers
      // sometimes need a metadata roundtrip. Falls back to 0 silently.
      let duration = 0;
      try {
        duration = await new Promise((resolve) => {
          const a = new Audio();
          a.preload = "metadata";
          a.onloadedmetadata = () => resolve(Number.isFinite(a.duration) ? a.duration : 0);
          a.onerror = () => resolve(0);
          a.src = b64;
        });
      } catch { /* ignore */ }
      setDraft((d) => ({ ...d, audio_b64: b64, duration_sec: duration }));
    } catch {
      toast.error("Could not read that audio file.");
    } finally {
      e.target.value = "";
    }
  };

  const clearAudio = () => {
    setDraft((d) => ({ ...d, audio_b64: "", duration_sec: 0 }));
  };

  if (log === null) {
    return <div className="text-[#555] text-xs tracking-widest uppercase">::: cueing tape :::</div>;
  }
  if (log === false) {
    return (
      <div className="border border-[#333] p-12 text-center" data-testid="voice-not-found">
        <div className="font-serif-display text-3xl mb-4">No such voice.</div>
        <button className="tiv-btn" onClick={() => navigate("/voices")}>Return to the hundred</button>
      </div>
    );
  }

  const view = editing ? draft : log;
  const hasAudio = Boolean(view.audio_b64);

  return (
    <div className="space-y-10" data-testid="voice-detail-page">
      {/* Breadcrumb */}
      <div className="text-[0.65rem] tracking-[0.35em] uppercase text-[#A3A3A3]">
        <Link to="/voices" className="hover:text-[#E3E1D9]">↖ Voices</Link>
        <span className="mx-3 text-[#555]">/</span>
        <span>{log.id}</span>
      </div>

      {/* Header */}
      <header className="border-b border-[#333] pb-8 space-y-4">
        <div className="flex flex-wrap items-baseline gap-4">
          <span className="font-mono-data text-[0.7rem] tracking-[0.3em] uppercase text-[#A3A3A3]">
            {log.id}
          </span>
          {log.duration_sec > 0 && (
            <span className="text-[0.6rem] tracking-[0.4em] uppercase text-[#555]" data-testid="voice-duration">
              {fmtDuration(log.duration_sec)}
            </span>
          )}
          {log.updated_at && (
            <span className="text-[0.55rem] tracking-[0.3em] uppercase text-[#555] ml-auto">
              updated {log.updated_at.split("T")[0]}
            </span>
          )}
        </div>
        {editing ? (
          <input
            className="tiv-input text-4xl"
            value={draft.label}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            placeholder="label this voice (e.g. the first descent)"
            data-testid="voice-edit-label"
          />
        ) : (
          <h1 className="font-serif-display text-5xl sm:text-6xl tracking-tighter leading-none">
            {log.label || <span className="text-[#555] italic">unlabelled</span>}
          </h1>
        )}
      </header>

      {/* Audio player */}
      <section data-testid="voice-audio-section">
        <div className="section-label">
          <span>Recording · Listen</span>
          <span>{hasAudio ? "◉ live" : "no tape"}</span>
        </div>
        {hasAudio ? (
          <div className="border border-[#333] bg-[#0a0a0a] p-5">
            <audio
              ref={audioRef}
              controls
              src={view.audio_b64}
              className="w-full"
              preload="metadata"
              data-testid="voice-audio-player"
            />
            {editing && (
              <button
                className="tiv-btn tiv-btn-danger text-[0.6rem] mt-3"
                onClick={clearAudio}
                data-testid="voice-clear-audio-btn"
              >
                Burn the Tape
              </button>
            )}
          </div>
        ) : (
          <div className="border border-dashed border-[#333] p-10 text-center text-[#555] text-xs tracking-widest uppercase">
            ::: no recording attached · subtitle only :::
          </div>
        )}
        {editing && !hasAudio && (
          <label
            className="block mt-4 border border-dashed border-[#333] p-6 text-center cursor-crosshair hover:border-[#E3E1D9]"
            data-testid="voice-audio-upload-label"
          >
            <input
              type="file"
              accept="audio/*"
              onChange={onAudioUpload}
              className="hidden"
              data-testid="voice-audio-upload-input"
            />
            <span className="text-xs tracking-[0.3em] uppercase text-[#A3A3A3]">
              + Affix Recording (max ~9 MB · MP3 / OGG / WAV)
            </span>
          </label>
        )}
      </section>

      {/* Transcript */}
      <section data-testid="voice-transcript-section">
        <div className="section-label">
          <span>Subtitle Log · For the Page-Bound</span>
          <span>§ transcriptio</span>
        </div>
        {editing ? (
          <textarea
            className="tiv-textarea"
            style={{ minHeight: "240px" }}
            value={draft.transcript}
            onChange={(e) => setDraft({ ...draft, transcript: e.target.value })}
            placeholder="type out the voice... line by line, breath by breath..."
            data-testid="voice-edit-transcript"
          />
        ) : log.transcript ? (
          <div
            className="text-base leading-loose whitespace-pre-wrap font-serif-display max-w-3xl"
            data-testid="voice-transcript-body"
          >
            {log.transcript}
          </div>
        ) : (
          <div className="border border-dashed border-[#333] p-10 text-center text-[#555] text-xs tracking-widest uppercase">
            ::: no transcript yet · {isAdmin ? "scribe one below" : "the voice has not been written down"} :::
          </div>
        )}
      </section>

      {/* Admin actions */}
      {isAdmin && (
        <div className="flex flex-wrap gap-3 border-t border-[#333] pt-8">
          {editing ? (
            <>
              <button
                className="tiv-btn"
                onClick={save}
                disabled={saving}
                data-testid="voice-save-btn"
              >
                {saving ? "Sealing..." : "Seal & Save"}
              </button>
              <button
                className="tiv-btn"
                onClick={cancel}
                data-testid="voice-cancel-btn"
              >
                Discard
              </button>
            </>
          ) : (
            <button
              className="tiv-btn"
              onClick={startEdit}
              data-testid="voice-edit-btn"
            >
              Inscribe / Edit
            </button>
          )}
        </div>
      )}
    </div>
  );
}
